import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Avatar, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Divider, Stack, CircularProgress,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    PendingActions as PendingActionsIcon,
    CheckCircleOutline as ApprovedIcon,
    CancelOutlined as RejectedIcon,
    ArrowForward as ArrowIcon,
    TrendingUp as TrendingIcon,
    Business as DeptIcon,
} from '@mui/icons-material';
import { axiosInstance } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

function StatCard({ label, value, subtitle, icon: Icon, gradient, onClick }) {
    return (
        <Card onClick={onClick} sx={{
            borderRadius: 3,
            cursor: onClick ? 'pointer' : 'default',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: gradient,
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': onClick ? { transform: 'translateY(-3px)', boxShadow: '0 8px 28px rgba(0,0,0,0.14)' } : {},
        }}>
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.8)', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {label}
                        </Typography>
                        <Typography sx={{ fontSize: 34, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>{subtitle}</Typography>
                        )}
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.25, flexShrink: 0 }}>
                        <Icon sx={{ fontSize: 24, color: '#fff' }} />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

const STATUS_COLORS = {
    Draft:             { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
    Submitted:         { bg: '#eff6ff', color: '#3b82f6', border: '#93c5fd' },
    Approved:          { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
    Rejected:          { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    'Pending Level 2': { bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
};

function StatusChip({ status }) {
    const c = STATUS_COLORS[status] || STATUS_COLORS.Draft;
    const label = status === 'Pending Level 2' ? 'Pending External Approval' : status;
    return <Chip label={label} size="small" sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 600, fontSize: 11 }} />;
}

export default function HRTimesheetOverview({ onTabChange, refreshKey }) {
    const [stats, setStats] = useState(null);
    const [recentTimesheets, setRecentTimesheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [statsRes, tsRes] = await Promise.all([
                    axiosInstance.get(API_ENDPOINTS.getHRTimesheetStats()),
                    axiosInstance.get(API_ENDPOINTS.getAllTimesheets('', '', '', '', '', 1, 50)),
                ]);
                setStats(statsRes.data?.result?.data ?? null);
                const tsData = tsRes.data?.result?.data ?? [];
                setRecentTimesheets(tsData);
            } catch (err) {
                console.error('Error fetching HR overview:', err);
                setError('Failed to load overview data.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [refreshKey]);

    // Stat values from API
    const total    = stats?.byStatus?.reduce((s, b) => s + b.count, 0) ?? 0;
    const pending  = stats?.pendingApproval ?? 0;
    const approved = stats?.byStatus?.find(s => s.status === 'Approved')?.count ?? 0;
    const rejected = stats?.byStatus?.find(s => s.status === 'Rejected')?.count ?? 0;

    // Department breakdown from fetched timesheets
    const deptMap = {};
    recentTimesheets.forEach(t => {
        const d = t.employeeDept ?? 'Unknown';
        if (!deptMap[d]) deptMap[d] = { total: 0, approved: 0, pending: 0 };
        deptMap[d].total++;
        if (t.status === 'Approved') deptMap[d].approved++;
        if (t.status === 'Submitted' || t.status === 'Pending Level 2') deptMap[d].pending++;
    });
    const deptRows = Object.entries(deptMap)
        .map(([dept, d]) => ({ dept, ...d }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);

    // Recent submissions (last 6)
    const recent = [...recentTimesheets]
        .filter(t => t.submittedOn)
        .sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn))
        .slice(0, 6);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#ef4444', fontSize: 14 }}>{error}</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Stat Cards */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total This Month" value={total} subtitle="Across all departments"
                        icon={AccessTimeIcon} gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
                        onClick={() => onTabChange(1, 'All')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Pending Approval" value={pending} subtitle="Awaiting manager action"
                        icon={PendingActionsIcon} gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        onClick={() => onTabChange(1, 'Submitted')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Approved This Month" value={stats?.approvedThisMonth ?? approved} subtitle="Final approvals this month"
                        icon={ApprovedIcon} gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        onClick={() => onTabChange(1, 'Approved')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Rejected This Month" value={stats?.rejectedThisMonth ?? rejected} subtitle="Returned to employees"
                        icon={RejectedIcon} gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                        onClick={() => onTabChange(1, 'Rejected')} />
                </Grid>
            </Grid>

            <Grid container spacing={2.5}>
                {/* Department Breakdown */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DeptIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>Department Breakdown</Typography>
                            </Box>
                            <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onTabChange(1)}
                                sx={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textTransform: 'none' }}>
                                View All
                            </Button>
                        </Box>
                        {deptRows.length === 0 ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}>
                                <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>No department data available.</Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['Department', 'Total', 'Approved', 'Pending'].map(h => (
                                                <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {deptRows.map(row => (
                                            <TableRow key={row.dept} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '&:last-child td': { border: 0 } }}>
                                                <TableCell sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{row.dept}</TableCell>
                                                <TableCell sx={{ fontSize: 13 }}>{row.total}</TableCell>
                                                <TableCell>
                                                    <Chip label={row.approved} size="small"
                                                        sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: 11, minWidth: 28 }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={row.pending} size="small"
                                                        sx={{ bgcolor: row.pending > 0 ? '#fff7ed' : '#f1f5f9', color: row.pending > 0 ? '#ea580c' : '#64748b', fontWeight: 700, fontSize: 11, minWidth: 28 }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>

                {/* Recent Submissions */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>Recent Submissions</Typography>
                            </Box>
                            <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onTabChange(1)}
                                sx={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textTransform: 'none' }}>
                                View All
                            </Button>
                        </Box>
                        {recent.length === 0 ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}>
                                <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>No recent submissions.</Typography>
                            </Box>
                        ) : (
                            <Box>
                                {recent.map((ts, i) => (
                                    <Box key={ts.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75 }}>
                                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#6366f1', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                                {ts.employeeAvatar}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{ts.employeeName}</Typography>
                                                    {ts.employeeCode && (
                                                        <Chip label={ts.employeeCode} size="small"
                                                            sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontSize: 10, fontWeight: 700, height: 17 }} />
                                                    )}
                                                </Stack>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                                    {ts.employeeDept} · {ts.weekStart} – {ts.weekEnd}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <StatusChip status={ts.status} />
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.5 }}>{ts.totalHours}h total</Typography>
                                            </Box>
                                        </Box>
                                        {i < recent.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
